import React, { useContext } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import StorefrontIcon from '@material-ui/icons/Storefront';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import UIContainer from '../UI/Container/UIContainer';
import StoreContext from '../Store/context';
import { usePOS } from '../../contexts/POSContext';
import { formattedCurrency } from '../../utils/utils';
import POSItemThumbnail from './POSItemThumbnail';

const POSQuantityStepper = ({ value, onIncrement, onDecrement, onCommit, isDarkMode }) => {
  const [localVal, setLocalVal] = React.useState(String(value ?? 1));
  const [isLocked, setIsLocked] = React.useState(false);
  const lockTimerRef = React.useRef(null);

  React.useEffect(() => {
    setLocalVal(String(value ?? 1));
  }, [value]);

  React.useEffect(() => {
    return () => {
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    };
  }, []);

  const triggerLock = () => {
    setIsLocked(true);
    if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    lockTimerRef.current = setTimeout(() => {
      setIsLocked(false);
    }, 350);
  };

  const handleDecClick = () => {
    if (isLocked) return;
    triggerLock();
    onDecrement();
  };

  const handleIncClick = () => {
    if (isLocked) return;
    triggerLock();
    onIncrement();
  };

  const handleBlur = () => {
    const clean = String(localVal).trim().replace(',', '.');
    const num = parseFloat(clean);
    if (!isNaN(num) && num > 0) {
      const rounded = Math.round(num * 1000) / 1000;
      setLocalVal(String(rounded));
      onCommit(rounded);
    } else {
      setLocalVal(String(value || 1));
      onCommit(value || 1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    if (/^[0-9]*[.,]?[0-9]*$/.test(v) || v === '') {
      setLocalVal(v);
    }
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
        borderRadius: 6,
        padding: '2px 3px',
        border: isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0',
      }}
    >
      <IconButton
        size="small"
        onClick={handleDecClick}
        disabled={isLocked || Number(value) <= 1}
        style={{
          padding: 3,
          backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
          borderRadius: 4,
          boxShadow: isDarkMode ? 'none' : '0 1px 2px rgba(0,0,0,0.08)',
          pointerEvents: isLocked ? 'none' : 'auto',
          opacity: (isLocked || Number(value) <= 1) ? 0.35 : 1,
        }}
        title="Diminuir quantidade"
      >
        <RemoveIcon style={{ fontSize: 13, color: isDarkMode ? '#94a3b8' : '#475569' }} />
      </IconButton>
      <TextField
        type="text"
        size="small"
        value={localVal}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        inputProps={{
          inputMode: 'decimal',
          style: {
            fontSize: 13,
            fontWeight: 700,
            textAlign: 'center',
            width: 38,
            padding: '3px 2px',
            color: isDarkMode ? '#f8fafc' : '#1e293b',
          },
        }}
        InputProps={{
          disableUnderline: true,
        }}
      />
      <IconButton
        size="small"
        onClick={handleIncClick}
        disabled={isLocked}
        style={{
          padding: 3,
          backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
          borderRadius: 4,
          boxShadow: isDarkMode ? 'none' : '0 1px 2px rgba(0,0,0,0.08)',
          pointerEvents: isLocked ? 'none' : 'auto',
          opacity: isLocked ? 0.6 : 1,
        }}
        title="Aumentar quantidade"
      >
        <AddIcon style={{ fontSize: 13, color: isDarkMode ? '#94a3b8' : '#475569' }} />
      </IconButton>
    </div>
  );
};

const POSCartTable = () => {
  const { credencials } = useContext(StoreContext);
  const { cartItems, updateItemAmount, removeItem, isDarkMode } = usePOS();
  const lastActionTimeRef = React.useRef(0);

  const companyLogo =
    credencials?.companyData?.logo_url ||
    credencials?.companyData?.logoUrl ||
    credencials?.companyData?.logo ||
    credencials?.companyData?.image ||
    credencials?.image;

  const companyName =
    credencials?.companyData?.pseudonimo ||
    credencials?.companyData?.name ||
    '';

  const cellBg = isDarkMode ? '#151d30' : '#ffffff';
  const cellColor = isDarkMode ? '#94a3b8' : undefined;

  return (
    <UIContainer
      container
      style={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        padding: '8px 16px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: isDarkMode ? '#151d30' : '#ffffff',
      }}
    >
      {/* Marca d'água da Logo/Empresa com sangria total sem espaços brancos */}
      <div
        className="pos-cart-watermark"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          userSelect: 'none',
          zIndex: 0,
          overflow: 'hidden',
          borderRadius: 5,
        }}
      >
        {companyLogo ? (
          <img
            src={companyLogo}
            alt={companyName || 'Empresa'}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) scale(1.12)',
              minWidth: '100%',
              minHeight: '100%',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center center',
              opacity: isDarkMode ? 0.055 : 0.065,
              filter: isDarkMode ? 'blur(6px)' : 'blur(2.5px)',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isDarkMode ? 0.08 : 0.06,
              filter: 'blur(1.5px)',
              gap: 12,
            }}
          >
            <StorefrontIcon style={{ fontSize: 160, color: isDarkMode ? '#38bdf8' : '#17497F' }} />
            {companyName && (
              <span
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 900,
                  color: isDarkMode ? '#38bdf8' : '#17497F',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                }}
              >
                {companyName}
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: isDarkMode ? '#90caf9' : '#17497F' }}>
          Itens no Carrinho ({cartItems.length})
        </span>
      </div>

      <div className="pos-table-scroll-container" style={{ position: 'relative', zIndex: 1, backgroundColor: 'transparent' }}>
        {cartItems.length === 0 ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: isDarkMode ? '#64748b' : '#9e9e9e',
              gap: 8,
              minHeight: '220px',
            }}
          >
            <ShoppingCartOutlinedIcon style={{ fontSize: 48, color: isDarkMode ? '#475569' : '#bdbdbd' }} />
            <div style={{ fontSize: '1rem', fontWeight: 600, color: isDarkMode ? '#cbd5e1' : '#757575' }}>
              Nenhum item adicionado
            </div>
            <div style={{ fontSize: '0.82rem', color: isDarkMode ? '#64748b' : '#9e9e9e' }}>
              Passe o leitor de código de barras ou pressione <strong>F2</strong> para buscar
            </div>
          </div>
        ) : (
          <TableContainer style={{ maxHeight: '100%', backgroundColor: 'transparent' }}>
            <Table size="small" stickyHeader style={{ backgroundColor: 'transparent' }}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 115, fontWeight: 700, backgroundColor: cellBg, color: cellColor }}>Qntd.</TableCell>
                  <TableCell style={{ fontWeight: 700, backgroundColor: cellBg, color: cellColor }}>Produto / Serviço</TableCell>
                  <TableCell style={{ width: 110, fontWeight: 700, backgroundColor: cellBg, color: cellColor }}>Valor un.</TableCell>
                  <TableCell style={{ width: 130, fontWeight: 700, backgroundColor: cellBg, color: cellColor }}>Valor total</TableCell>
                  <TableCell style={{ width: 60, align: 'center', fontWeight: 700, backgroundColor: cellBg, color: cellColor }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cartItems.map((item, index) => {
                  const hasDiscount = Number(item.discountValue || item.discount) > 0;
                  const originalTotal = (item.amount || 1) * (item.valueUn || 0);

                  return (
                    <TableRow
                      key={`${item.id}-${item.typeItem}-${index}`}
                      hover
                      style={{ backgroundColor: isDarkMode ? 'rgba(21, 29, 48, 0.75)' : 'rgba(255, 255, 255, 0.75)' }}
                    >
                      <TableCell style={{ width: 115 }}>
                        <POSQuantityStepper
                          value={item.amount}
                          isDarkMode={isDarkMode}
                          onIncrement={() => {
                            const currentAmt = Number(item.amount || 0);
                            updateItemAmount(index, Math.round((currentAmt + 1) * 1000) / 1000);
                          }}
                          onDecrement={() => {
                            const currentAmt = Number(item.amount || 1);
                            if (currentAmt > 1) {
                              updateItemAmount(index, Math.round((currentAmt - 1) * 1000) / 1000);
                            }
                          }}
                          onCommit={(newQty) => updateItemAmount(index, newQty)}
                        />
                      </TableCell>

                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <POSItemThumbnail
                            images={item.images}
                            name={item.name}
                            typeItem={item.typeItem}
                            size={40}
                            isDarkMode={isDarkMode}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: isDarkMode ? '#f8fafc' : '#212121', fontSize: '0.88rem', wordBreak: 'break-word' }}>
                              {item.name}
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '0.72rem', color: isDarkMode ? '#94a3b8' : '#757575' }}>
                                {item.barCode ? `Cód: ${item.barCode}` : `ID: #${item.id}`}
                              </span>
                              {hasDiscount && (
                                <span
                                  style={{
                                    fontSize: '0.7rem',
                                    color: isDarkMode ? '#86efac' : '#2e7d32',
                                    backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.2)' : '#e8f5e9',
                                    padding: '1px 5px',
                                    borderRadius: 3,
                                    fontWeight: 700,
                                  }}
                                >
                                  Desc: -{formattedCurrency(item.discountValue || item.discount)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e1' : '#424242' }}>
                        {formattedCurrency(item.valueUn)}
                      </TableCell>

                      <TableCell style={{ fontSize: '0.88rem', fontWeight: 700, color: isDarkMode ? '#4ade80' : '#185421' }}>
                        {formattedCurrency(item.valueTotal)}
                        {hasDiscount && (
                          <div style={{ fontSize: '0.72rem', color: isDarkMode ? '#64748b' : '#9e9e9e', textDecoration: 'line-through' }}>
                            {formattedCurrency(originalTotal)}
                          </div>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title="Remover item do carrinho">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const now = Date.now();
                              if (now - lastActionTimeRef.current < 250) return;
                              lastActionTimeRef.current = now;
                              removeItem(index);
                            }}
                            style={{ color: '#ef4444', padding: 4 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </UIContainer>
  );
};

export default POSCartTable;
